import React, { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API_BASE_URL, extractErrorMessage } from "../../../../api";

const ForgotPasswordModal = ({ onClose }) => {
    const { t } = useTranslation();
    const [step, setStep] = useState("request");
    const [username, setUsername] = useState("");
    const [otp, setOtp] = useState("");
    const [resetToken, setResetToken] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [requestMessage, setRequestMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            if (step === "request") {
                const response = await axios.post(`${API_BASE_URL}/api/password-reset/request`, {
                    username: username.trim(),
                });
                const channel = response.data?.channel || "email";
                setRequestMessage(response.data?.message || "");

                if (channel === "phone") {
                    setStep("otp");
                } else {
                    setStep("email-success");
                }
            } else if (step === "otp") {
                const response = await axios.post(`${API_BASE_URL}/api/auth/phone/forgot-password/verify-otp`, {
                    username: username.trim(),
                    otp: otp.trim(),
                });
                setResetToken(response.data?.resetToken || "");
                setStep("reset");
            } else {
                await axios.post(`${API_BASE_URL}/api/auth/phone/forgot-password/reset`, {
                    token: resetToken,
                    password,
                    confirmPassword,
                });
                setStep("phone-success");
            }
        } catch (err) {
            setError(extractErrorMessage(err, t("Une erreur est survenue.")));
        } finally {
            setLoading(false);
        }
    };

    const isSuccess = step === "email-success" || step === "phone-success";

    const renderSuccess = () => {
        if (step === "phone-success") {
            return (
                <div className="cm-success">
                    <div className="cm-success-ring">
                        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
                            <circle cx="14" cy="14" r="12" stroke="rgba(42,92,169,.2)" strokeWidth="1.5" />
                            <polyline className="cm-check-path" points="8,14 12,18 20,10" fill="none" stroke="#2a5ca9" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <div className="cm-success-badge">{t('Réinitialisation terminée')}</div>
                    <h3 className="cm-success-title">{t('Mot de passe modifié')}</h3>
                    <p className="cm-success-desc">
                        {t('Votre mot de passe a été modifié.')}<br />
                        {t("Connectez-vous avec votre nom d'utilisateur et votre nouveau mot de passe.")}
                    </p>
                    <button className="cm-btn" type="button" onClick={onClose}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                        </svg>
                        {t('Retour à la connexion')}
                    </button>
                </div>
            );
        }

        return (
            <div className="cm-success">
                <div className="cm-success-ring">
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
                        <circle cx="14" cy="14" r="12" stroke="rgba(42,92,169,.2)" strokeWidth="1.5" />
                        <polyline className="cm-check-path" points="8,14 12,18 20,10" fill="none" stroke="#2a5ca9" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
                <div className="cm-success-badge">{t('Email envoyé')}</div>
                <h3 className="cm-success-title">{t('Vérifiez votre boîte mail !')}</h3>
                <p className="cm-success-desc">{requestMessage}<br />{t('Pensez à vérifier vos spams si besoin.')}</p>
                <button className="cm-btn" type="button" onClick={onClose}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                    </svg>
                    {t('Compris, retour à la connexion')}
                </button>
            </div>
        );
    };

    return (
        <div className="cm-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="cm-card">
                <div className="cm-head">
                    <div className="cm-head-icon">
                        {isSuccess ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="M20 4H4C2.9 4 2 4.9 2 6v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="rgba(255,255,255,0.9)" />
                            </svg>
                        ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="M12 1C8.676 1 6 3.676 6 7v1H4a1 1 0 00-1 1v13a1 1 0 001 1h16a1 1 0 001-1V9a1 1 0 00-1-1h-2V7c0-3.324-2.676-6-6-6zm0 2c2.276 0 4 1.724 4 4v1H8V7c0-2.276 1.724-4 4-4zm0 9a2 2 0 110 4 2 2 0 010-4z" fill="rgba(255,255,255,0.9)" />
                            </svg>
                        )}
                    </div>
                    <h2 className="cm-head-title">{isSuccess ? (step === "phone-success" ? t('Réinitialisation terminée') : t('Lien envoyé')) : t('Mot de passe oublié')}</h2>
                    <p className={`cm-head-sub${isSuccess ? " sent" : ""}`}>
                        {step === "otp"
                            ? t('Saisissez le code reçu par SMS')
                            : step === "reset"
                                ? t('Saisissez votre nouveau mot de passe')
                                : isSuccess
                                    ? t('Votre demande a bien été traitée')
                                    : t('Saisissez votre identifiant pour recevoir un lien de réinitialisation')}
                    </p>
                    <button className="cm-close" type="button" onClick={onClose} aria-label={t('Fermer')}>
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                            <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>

                <div className="cm-body">
                    {!isSuccess ? (
                        <form onSubmit={handleSubmit}>
                            {error && <div className="cm-err">{error}</div>}

                            {step === "request" && (
                                <>
                                    <label className="cm-label" htmlFor="reset-identifier">{t("Nom d'utilisateur")}</label>
                                    <div className="cm-field">
                                        <input
                                            id="reset-identifier"
                                            className="cm-input"
                                            type="text"
                                            placeholder={t('Email ou téléphone (+212…)')}
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                            disabled={loading}
                                            autoFocus
                                        />
                                    </div>
                                </>
                            )}

                            {step === "otp" && (
                                <>
                                    <label className="cm-label" htmlFor="reset-otp">{t('Code reçu par SMS')}</label>
                                    <div className="cm-field">
                                        <input
                                            id="reset-otp"
                                            className="cm-input"
                                            type="text"
                                            placeholder="123456"
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value)}
                                            disabled={loading}
                                            autoFocus
                                        />
                                    </div>
                                </>
                            )}

                            {step === "reset" && (
                                <>
                                    <label className="cm-label" htmlFor="reset-password">{t('Nouveau mot de passe')}</label>
                                    <div className="cm-field">
                                        <input
                                            id="reset-password"
                                            className="cm-input"
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            disabled={loading}
                                            autoFocus
                                        />
                                    </div>
                                    <label className="cm-label" htmlFor="reset-confirm-password">{t('Confirmation')}</label>
                                    <div className="cm-field">
                                        <input
                                            id="reset-confirm-password"
                                            className="cm-input"
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            disabled={loading}
                                        />
                                    </div>
                                </>
                            )}

                            <button
                                type="submit"
                                className="cm-btn"
                                disabled={loading || (step === "request" && !username.trim()) || (step === "otp" && !otp.trim())}
                            >
                                {loading ? <><span className="cm-spin" />{t('Envoi en cours...')}</> : (
                                    <>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                                        </svg>
                                        {step === "request" ? t('Continuer') : step === "otp" ? t('Vérifier') : t('Réinitialiser')}
                                    </>
                                )}
                            </button>
                        </form>
                    ) : renderSuccess()}
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordModal;
